import React from 'react'
import * as Yup from 'yup'
import { Field, Form, Formik, ErrorMessage } from 'formik'
import { Row } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import CustomTextarea from '../../components/_shared/TextArea/TextArea'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
    addCommentToIncident,
    getIncidentById,
} from '../../../utils/api/incidentsAPI'
import toasters from '../../../utils/toasters/Toasters'
import RotatingLinesLoader from '../../components/_shared/Loaders/RotatingLinesLoader'
import CloseIncidentIcon from '../../assets/svg/CloseIncidentIcon'
import UnableLoad from '../../assets/svg/UnableLoad'
import InvalidLink from '../../assets/svg/InvalidLink'

const validationSchema = Yup.object({
    note_tx: Yup.string().required('Comment is required'),
})

const CommentsOnIncident = () => {
    const [searchParams] = useSearchParams()
    const { token } = useSelector((state) => state?.auth ?? {})
    // Support both 'id' (incident_id) and legacy 'contactLinkId' for incident id
    const incident_id = searchParams.get('id') || searchParams.get('incidentId')
    const contact_link_id = searchParams.get('contactLinkId')

    // Only fetch incident when user is logged in (GET /incidents/:id requires auth).
    // When not logged in, show comment form and rely on add_comment API for validation.
    const isLoggedIn = !!token
    const {
        data: incidentData,
        isLoading: isLoadingIncident,
        isError: isIncidentError,
    } = useQuery(
        ['comments-on-incident-incident', incident_id],
        () => getIncidentById(incident_id),
        { enabled: !!incident_id && isLoggedIn, retry: false }
    )

    const incidentStatus =
        incidentData?.data?.[0]?.ref_status_cd ||
        incidentData?.[0]?.ref_status_cd
    const isIncidentClosed = isLoggedIn && incidentStatus === 'CLOSED'

    const handleSubmit = async (values, { resetForm }) => {
        if (isIncidentClosed || !incident_id) return
        const payload = {
            comment_tx: values.note_tx,
            contact_link_id,
        }

        try {
            const response = await addCommentToIncident(incident_id, payload)
            toasters({ type: 'success', message: 'Comment added successfully' })
            resetForm()
        } catch (error) {
            toasters({
                type: 'error',
                message:
                    error?.response?.data?.message || 'Something went wrong!',
            })
        }
    }

    if (!incident_id) {
        return (
            <div className="d-flex flex-column gap-2 justify-content-center align-items-center mt-5">
                <div>
                   <InvalidLink />
                </div>
                <p className="my-3 text-center">Invalid link. Missing incident identifier.</p>
            </div>
        )
    }

    if (isLoggedIn && isLoadingIncident) {
        return <RotatingLinesLoader />
    }

    if (isLoggedIn && isIncidentError) {
        return (
            <div className="d-flex flex-column gap-2 justify-content-center align-items-center mt-5">
                <div>
                    <UnableLoad />
                </div>
                <p className="my-3 text-center">Unable to load incident details.</p>
            </div>
        )
    }

    if (isIncidentClosed) {
        return (
            <div className="d-flex flex-column gap-2 justify-content-center align-items-center mt-5">
               
                    <div className="">
                        <CloseIncidentIcon />
                    </div>
                    <p className="my-3 text-center">
                        Cannot add comment on a closed incident.
                    </p>
                
            </div>
        )
    }

    return (
        <div className="d-flex justify-content-center align-items-center mt-5 commentsOnIncident">
            <Formik
                initialValues={{ note_tx: '' }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched }) => (
                    <Form>
                        <Row>
                            <Field
                                name="note_tx"
                                labelText={
                                    'Enter your comment and click Submit.'
                                }
                                type="text"
                                values={values}
                                touched={touched}
                                errors={errors}
                                rows={10}
                                component={CustomTextarea}
                            />

                            <ErrorMessage
                                name="note_tx"
                                component="div"
                                className="text-danger mt-1"
                            />
                            <div className="d-flex justify-content-end align-items-center mt-3 gap-3">
                                {/* <Link
                                    to="/"
                                    className="btn btn-outline-secondary"
                                >
                                    Weatec Home
                                </Link> */}

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    Submit
                                </button>
                            </div>
                        </Row>
                    </Form>
                )}
            </Formik>
        </div>
    )
}

export default CommentsOnIncident
